<template>
  <v-container
    id="qr-provisioning-view"
    fluid
    tag="section"
  >
    <material-card
      icon="mdi-qrcode-edit"
      icon-small
      color="primary"
      :title="`${selectedCompanyName || 'Company'} QR Provisioning`"
    >
      <v-card-text>
        <div class="d-flex align-center flex-wrap mb-2">
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            class="qr-search-field"
            clearable
            hide-details
            label="Search records"
            single-line
          />
          <v-spacer />
          <app-btn
            :loading="loading"
            @click="reload"
          >
            <v-icon class="mr-1">
              mdi-refresh
            </v-icon>
            Refresh
          </app-btn>
        </div>

        <v-data-table
          :headers="headers"
          :items="tableItems"
          :options.sync="options"
          :server-items-length="tableServerItemsLength"
          :loading="loading"
          no-results-text="No QR provisioning records match your search."
          disable-sort
          :footer-props="{
            showFirstLastPage: true,
            'items-per-page-options': [5, 10, 15, 25],
          }"
        >
          <template v-slot:[`item.isUsed`]="{ item }">
            <v-chip
              small
              :color="item.isUsed ? 'grey' : 'success'"
              text-color="white"
            >
              {{ item.isUsed ? 'Used' : 'Available' }}
            </v-chip>
          </template>

          <template v-slot:[`item.isUsedAt`]="{ item }">
            {{ formatDate(item.isUsedAt) }}
          </template>

          <template v-slot:[`item.expiryDate`]="{ item }">
            <span :class="{ 'error--text': isExpired(item.expiryDate) }">
              {{ formatDate(item.expiryDate) }}
              <span v-if="isExpired(item.expiryDate)">(expired)</span>
            </span>
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-icon
              medium
              color="info"
              @click="editItem(item)"
            >
              mdi-pencil
            </v-icon>
          </template>

          <template v-slot:top>
            <v-dialog
              v-model="dialog"
              max-width="480px"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Update QR Provisioning</span>
                </v-card-title>

                <v-card-text>
                  <div
                    v-if="editedItem.deviceName"
                    class="text-body-2 grey--text text--darken-1 mb-4"
                  >
                    Device: <strong>{{ editedItem.deviceName }}</strong>
                  </div>

                  <v-switch
                    v-model="editedItem.isUsed"
                    inset
                    :label="editedItem.isUsed ? 'Used' : 'Available'"
                  />

                  <v-text-field
                    v-model="editedItem.expiryDate"
                    type="datetime-local"
                    outlined
                    dense
                    label="Expiry Date"
                    hint="The QR can no longer enroll devices after this time."
                    persistent-hint
                  />
                </v-card-text>

                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    text
                    @click="close"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    :loading="saving"
                    @click="save"
                  >
                    Save
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  import { get } from 'vuex-pathify'

  const pad = (value) => String(value).padStart(2, '0')

  export default {
    name: 'QrProvisioningView',

    data: () => ({
      items: [],
      loading: false,
      saving: false,
      dialog: false,
      serverItemsLength: 0,
      lastQueryKey: '',
      search: '',
      searchTerm: '',
      searchTimer: null,
      // Searching cannot be pushed to the API (List only takes Page/Limit), so
      // the whole list is pulled once and filtered/paged in the browser.
      allItems: [],
      allItemsLoaded: false,
      options: {
        page: 1,
        itemsPerPage: 10,
      },
      headers: [
        { text: 'Device Name', value: 'deviceName' },
        { text: 'Device Code', value: 'deviceCode' },
        { text: 'Status', value: 'isUsed', align: 'center' },
        { text: 'Used At', value: 'isUsedAt' },
        { text: 'Expiry Date', value: 'expiryDate' },
        { text: 'Actions', value: 'actions', align: 'end' },
      ],
      editedItem: {
        id: '',
        deviceName: '',
        isUsed: false,
        expiryDate: '',
      },
      defaultItem: {
        id: '',
        deviceName: '',
        isUsed: false,
        expiryDate: '',
      },
    }),

    computed: {
      ...get('company', ['selectedCompanyId', 'selectedCompanyName']),

      isSearching () {
        return !!this.searchTerm
      },

      filteredItems () {
        const term = this.searchTerm

        if (!term) {
          return this.allItems
        }

        return this.allItems.filter((item) => this.matchesSearch(item, term))
      },

      tableItems () {
        return this.isSearching ? this.filteredItems : this.items
      },

      tableServerItemsLength () {
        // -1 hands paging back to the data table while searching locally.
        return this.isSearching ? -1 : this.serverItemsLength
      },
    },

    watch: {
      options: {
        deep: true,
        immediate: true,
        handler () {
          this.loadList()
        },
      },
      search (value) {
        clearTimeout(this.searchTimer)
        this.searchTimer = setTimeout(() => {
          this.applySearch(value)
        }, 350)
      },
      selectedCompanyId () {
        this.allItems = []
        this.allItemsLoaded = false

        if (this.isSearching) {
          this.options.page = 1
          this.loadAllItems()
          return
        }

        if (this.options.page !== 1) {
          this.options.page = 1
        } else {
          this.loadList()
        }
      },
      dialog (val) {
        if (!val) {
          this.close()
        }
      },
    },

    beforeDestroy () {
      clearTimeout(this.searchTimer)
    },

    methods: {
      async loadList (force = false) {
        if (this.isSearching) {
          return
        }

        if (!this.selectedCompanyId) {
          this.items = []
          this.serverItemsLength = 0
          return
        }

        const { page, itemsPerPage } = this.options
        const queryKey = `${this.selectedCompanyId}|${page}|${itemsPerPage}`

        // The synced data-table options can emit duplicate updates; skip a
        // reload when nothing about the query actually changed.
        if (!force && queryKey === this.lastQueryKey) {
          return
        }
        this.lastQueryKey = queryKey

        this.loading = true

        try {
          const response = await this.$http.get('QrProvisioning/List', {
            params: {
              CompanyId: this.selectedCompanyId,
              Page: page,
              Limit: itemsPerPage,
            },
          })

          const responseData = response?.data
          const rows = Array.isArray(responseData)
            ? responseData
            : Array.isArray(responseData?.data)
              ? responseData.data
              : []

          this.items = rows

          const reportedTotal = Number(
            responseData?.total ??
            responseData?.totalCount ??
            responseData?.count,
          )

          if (!Number.isNaN(reportedTotal) && reportedTotal > 0) {
            this.serverItemsLength = reportedTotal
          } else {
            // The API returns a bare array with no total, so estimate one that
            // keeps the "next page" control enabled while full pages come back.
            const consumed = (page - 1) * itemsPerPage
            this.serverItemsLength = rows.length < itemsPerPage
              ? consumed + rows.length
              : consumed + rows.length + 1
          }
        } catch (error) {
          this.items = []
          this.serverItemsLength = 0
          this.$toast.error(
            this.getApiErrorMessage(error, 'Could not load QR provisioning records.'),
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.loading = false
        }
      },

      reload () {
        this.allItems = []
        this.allItemsLoaded = false

        if (this.isSearching) {
          this.loadAllItems()
          return
        }

        this.loadList(true)
      },

      applySearch (value) {
        const term = String(value || '').trim().toLowerCase()

        if (term === this.searchTerm) {
          return
        }

        this.searchTerm = term
        this.options.page = 1

        if (term) {
          this.loadAllItems()
        } else {
          this.loadList(true)
        }
      },

      // Walks the paged endpoint until it runs out of rows so the local filter
      // sees every record, not just the page currently on screen.
      async loadAllItems () {
        if (!this.selectedCompanyId) {
          this.allItems = []
          this.allItemsLoaded = true
          return
        }

        if (this.allItemsLoaded) {
          return
        }

        const pageSize = 100
        const maxRecords = 5000
        const rows = []

        this.loading = true

        try {
          for (let page = 1; rows.length < maxRecords; page += 1) {
            const response = await this.$http.get('QrProvisioning/List', {
              params: {
                CompanyId: this.selectedCompanyId,
                Page: page,
                Limit: pageSize,
              },
            })

            const responseData = response?.data
            const pageRows = Array.isArray(responseData)
              ? responseData
              : Array.isArray(responseData?.data)
                ? responseData.data
                : []

            rows.push(...pageRows)

            if (pageRows.length < pageSize) {
              break
            }
          }

          this.allItems = rows
          this.allItemsLoaded = true
        } catch (error) {
          this.allItems = []
          this.allItemsLoaded = false
          this.$toast.error(
            this.getApiErrorMessage(error, 'Could not load QR provisioning records.'),
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.loading = false
        }
      },

      matchesSearch (item, term) {
        const haystack = [
          item.deviceName,
          item.deviceCode,
          item.isUsed ? 'used' : 'available',
          this.formatDate(item.isUsedAt),
          this.formatDate(item.expiryDate),
        ]

        return haystack.some(
          (value) => String(value || '').toLowerCase().includes(term),
        )
      },

      editItem (item) {
        this.editedItem = {
          id: item.id,
          deviceName: item.deviceName,
          isUsed: !!item.isUsed,
          expiryDate: this.toDatetimeLocal(item.expiryDate),
        }
        this.dialog = true
      },

      close () {
        this.dialog = false
        this.$nextTick(() => {
          this.editedItem = Object.assign({}, this.defaultItem)
        })
      },

      async save () {
        if (!this.editedItem.id) {
          return
        }

        this.saving = true

        try {
          const payload = {
            id: this.editedItem.id,
            isUsed: this.editedItem.isUsed,
            expiryDate: this.fromDatetimeLocal(this.editedItem.expiryDate),
          }

          const response = await this.$http.put('QrProvisioning/UpdateState', payload)

          if (response?.data?.success === false) {
            this.$toast.error(
              response.data.message || 'Could not update the QR provisioning record.',
              'Error',
              { position: 'topRight' },
            )
            return
          }

          this.$toast.success('QR provisioning updated.', 'Success', { position: 'topRight' })
          this.close()
          this.allItemsLoaded = false
          if (this.isSearching) {
            this.loadAllItems()
          } else {
            this.loadList(true)
          }
        } catch (error) {
          this.$toast.error(
            this.getApiErrorMessage(error, 'Could not update the QR provisioning record.'),
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.saving = false
        }
      },

      formatDate (value) {
        // Naive UTC from the API -> local. `new Date(value)` would have read it
        // as local time and shown it off by the viewer's offset.
        return this.$format.dateTime(value) || '--'
      },

      isExpired (value) {
        return this.$format.isPast(value)
      },

      toDatetimeLocal (value) {
        const date = this.$format.toDate(value)
        if (!date) {
          return ''
        }

        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
      },

      fromDatetimeLocal (value) {
        if (!value) {
          return null
        }

        const date = new Date(value)
        return Number.isNaN(date.getTime()) ? null : date.toISOString()
      },

      getApiErrorMessage (error, fallbackMessage) {
        const status = error?.response?.status
        const message = error?.response?.data?.message || error?.response?.data?.error

        if (status === 401 || status === 403) {
          return message || 'This action requires the SuperUser role.'
        }

        return message || fallbackMessage
      },
    },
  }
</script>

<style scoped>
.qr-search-field {
  max-width: 320px;
}
</style>
